import { useEffect, useState, type MouseEvent } from 'react';
import { CalendarCheck, MessageSquare, Phone } from 'lucide-react';
import { useLang } from '@/lib/LanguageContext';
import { useCalendly } from '@/lib/CalendlyContext';
import { useLeadModal } from '@/lib/LeadModalContext';
import { getCookieConsent } from '@/components/ui/cookie-banner';
import { t } from '@/lib/translations';

/**
 * MobileStickyBar — 4 actions Phone/SMS/Calendly/CTA terracotta.
 * Mobile-only (md:hidden). Phone & SMS use tel:/sms: schemes; Calendly opens
 * the shared CalendlyModal via context; CTA scrolls to #contact.
 */
const PHONE = '+14383826496';

export function MobileStickyBar() {
  const { lang } = useLang();
  const { open: openCalendly } = useCalendly();
  const { openForm } = useLeadModal();

  // Phase 27.72B — Codex audit fix : ne s'affiche QUE si l'utilisateur a déjà
  // pris une décision cookie (accepté/refusé/customisé). Sinon, le cookie banner
  // + sticky bar + SmsFAB saturaient le bas écran sur mobile 390px. Le banner
  // prend priorité visuelle jusqu'au choix.
  const [consentDecided, setConsentDecided] = useState<boolean>(
    () => getCookieConsent() !== null
  );
  useEffect(() => {
    if (consentDecided) return;
    const handler = () => setConsentDecided(true);
    window.addEventListener('cookie-consent-change', handler);
    return () => window.removeEventListener('cookie-consent-change', handler);
  }, [consentDecided]);
  if (!consentDecided) return null;

  const callAria = typeof t.mobile.call_aria === 'string' ? t.mobile.call_aria : t.mobile.call_aria[lang];
  const smsAria = typeof t.mobile.sms_aria === 'string' ? t.mobile.sms_aria : t.mobile.sms_aria[lang];
  const smsBody = typeof t.mobile.sms_body === 'string' ? t.mobile.sms_body : t.mobile.sms_body[lang];
  const calendlyAria = typeof t.mobile.calendly_aria === 'string' ? t.mobile.calendly_aria : t.mobile.calendly_aria[lang];
  const ctaLabel = typeof t.mobile.cta_label === 'string' ? t.mobile.cta_label : t.mobile.cta_label[lang];

  const smsHref = `sms:${PHONE}?body=${encodeURIComponent(smsBody)}`;

  const handleCalendlyClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    openCalendly();
  };

  const iconBtn =
    'w-11 h-11 shrink-0 rounded-md border border-primary/30 text-primary inline-flex items-center justify-center transition-colors hover:bg-primary/5 active:bg-primary/10';

  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-background/95 backdrop-blur-md border-t border-border">
      {/* Phase 27.18 — combo L.2 rose-soft top accent line */}
      <div className="absolute inset-x-0 top-0 h-px bg-rose-soft/50" aria-hidden="true" />
      <div
        // Phase 27.79 a11y — WCAG 2.5.5 touch target spacing : gap-1.5 (6px)
        // → gap-2.5 (10px) entre les 4 boutons (Phone/SMS/Calendly/CTA).
        // Lighthouse mobile flag : "Target has insufficient size because it
        // is partially obscured (2px by 44px, should be 24px by 24px)".
        // gap-2.5 + w-11 (44px) chaque button = total spacing entre centres
        // dépasse 24px minimum WCAG.
        className="flex items-center gap-2.5 px-2.5 pt-2.5"
        style={{ paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom))' }}
      >
        <a href={`tel:${PHONE}`} className={iconBtn} aria-label={callAria}>
          <Phone className="w-4 h-4" strokeWidth={1.7} />
        </a>
        <a href={smsHref} className={iconBtn} aria-label={smsAria}>
          <MessageSquare className="w-4 h-4" strokeWidth={1.7} />
        </a>
        <a href="#" onClick={handleCalendlyClick} className={iconBtn} aria-label={calendlyAria}>
          <CalendarCheck className="w-4 h-4" strokeWidth={1.7} />
        </a>
        {/* Phase 27.89 — CTA ouvre le popup form (au lieu de scroll #contact). */}
        <button
          type="button"
          onClick={openForm}
          data-cursor="cta"
          // Phase 27.79-quinquies a11y : darker terracotta bg (L=0.51) pour WCAG AA pass.
          className="flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 py-2.5 px-2 bg-[oklch(0.51_0.135_38)] text-white font-bold rounded-md text-xs uppercase tracking-wide"
        >
          <span className="truncate">{ctaLabel}</span>
        </button>
      </div>
    </div>
  );
}
